/**
 * Offline capture queue — REQUIREMENTS.md §6.2.
 *
 * Matt photographs a receipt at a restaurant with one bar of signal. The
 * capture must not depend on the network: the photo goes into IndexedDB
 * immediately and uploads when the connection comes back.
 *
 * §6.2 rules out service-worker background sync for v1 — it has real gotchas
 * on iOS and isn't worth the debugging. So the drain runs in the page, on the
 * `online` event and at startup. The consequence is honest and worth stating
 * plainly in the UI: a queued capture uploads the next time the app is open
 * with a connection, not while the phone is in his pocket.
 *
 * The file bytes are stored as a Blob. IndexedDB stores Blobs natively in
 * every browser this app targets, so nothing is base64-inflated on the way in.
 */

const DB_NAME = 'budgetmonkey'
const DB_VERSION = 1
const STORE = 'pendingCaptures'

export interface PendingCapture {
  id: string
  userId: string
  fileName: string
  contentType: string
  sizeBytes: number
  captureSource: 'mobile_capture' | 'desktop_upload'
  blob: Blob
  queuedAt: string
  /**
   * Tie-breaker for `queuedAt`, which only has millisecond resolution — two
   * captures picked from one file dialog land in the same millisecond and
   * would otherwise drain in whatever order IndexedDB returned their random
   * keys. Resets per page load, which is harmless: across two page loads the
   * timestamps already differ.
   */
  seq: number
  /** Incremented each time a drain fails, to surface a capture that is stuck. */
  attempts: number
  lastError?: string
}

let sequence = 0

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('userId', 'userId', { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB unavailable'))
  })
}

function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode)
        const request = work(tx.objectStore(STORE))
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error ?? new Error('IndexedDB write failed'))
        tx.oncomplete = () => db.close()
      }),
  )
}

/** Whether the browser can queue at all. Private-mode Safari sometimes can't. */
export function queueAvailable(): boolean {
  return typeof indexedDB !== 'undefined'
}

/**
 * Whether the browser positively reports being offline.
 *
 * Never `!navigator.onLine`: outside a browser the property is undefined, and
 * the loose form reads "unknown" as offline, which strands the whole queue.
 *
 * A function rather than the inline comparison so that re-reading it after an
 * upload is a real re-read. TypeScript narrows the property from an earlier
 * guard and then calls the second comparison redundant — it isn't, because the
 * connection changing mid-drain is precisely what this asks about.
 */
export function isOffline(): boolean {
  return navigator.onLine === false
}

/**
 * Fired after a capture is queued.
 *
 * The uploader and the pending indicator are separate hooks on separate
 * screens' worth of state; without this the queue would sit at "0 waiting"
 * until something else happened to re-render it, which for a feature whose
 * entire job is reassurance would be worse than showing nothing.
 */
export const QUEUE_CHANGED_EVENT = 'budgetmonkey:queue-changed'

function announceChange(): void {
  // Storing the capture is the job; telling the UI is a courtesy. Guarded so a
  // missing DOM can never turn a successful write into a thrown error and lose
  // the photo it just saved.
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(QUEUE_CHANGED_EVENT))
}

export async function enqueueCapture(
  userId: string,
  file: File,
  captureSource: PendingCapture['captureSource'],
): Promise<PendingCapture> {
  const entry: PendingCapture = {
    id: crypto.randomUUID(),
    userId,
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
    captureSource,
    blob: file,
    queuedAt: new Date().toISOString(),
    seq: sequence++,
    attempts: 0,
  }
  await run('readwrite', (store) => store.add(entry))
  announceChange()
  return entry
}

export async function listPending(userId: string): Promise<PendingCapture[]> {
  const all = await run<PendingCapture[]>('readonly', (store) => store.getAll())
  return all
    .filter((entry) => entry.userId === userId)
    .sort(
      (a, b) =>
        a.queuedAt.localeCompare(b.queuedAt) || (a.seq ?? 0) - (b.seq ?? 0),
    )
}

export async function removePending(id: string): Promise<void> {
  await run('readwrite', (store) => store.delete(id))
  announceChange()
}

async function recordFailure(entry: PendingCapture, error: unknown): Promise<void> {
  await run('readwrite', (store) =>
    store.put({
      ...entry,
      attempts: entry.attempts + 1,
      lastError: error instanceof Error ? error.message : String(error),
    }),
  )
}

export interface DrainResult {
  uploaded: number
  failed: number
}

/**
 * Upload everything queued, oldest first.
 *
 * Sequential rather than parallel: this runs the moment a phone reconnects,
 * which is exactly when the connection is weakest, and eight simultaneous
 * multi-megabyte uploads on a recovering link fail more often than they
 * succeed. A capture that fails stays queued with its error recorded.
 */
export async function drainQueue(
  userId: string,
  upload: (capture: PendingCapture) => Promise<unknown>,
): Promise<DrainResult> {
  const pending = await listPending(userId)
  let uploaded = 0
  let failed = 0

  for (const entry of pending) {
    try {
      await upload(entry)
      await removePending(entry.id)
      uploaded += 1
    } catch (cause) {
      await recordFailure(entry, cause)
      failed += 1
      // Stop early only when the browser positively reports being offline —
      // grinding through the rest then just burns battery and inflates attempt
      // counts on captures that were never going to succeed this round. An
      // environment that doesn't report connectivity at all is treated as
      // online, so an unknown state never silently strands the queue.
      if (isOffline()) break
    }
  }

  return { uploaded, failed }
}

/** Rebuild a `File` from a queued entry so the normal upload path can take it. */
export function fileFromPending(entry: PendingCapture): File {
  return new File([entry.blob], entry.fileName, { type: entry.contentType })
}
